import { FC, useMemo, useState } from 'react';
import styles from './BuilderCollapse.module.scss';

export type CollapseProps = {
  items: CollapseItemProps[];
  allowMultiple: boolean;
  defaultOpen: number[];
};

export type CollapseItemProps = {
  title: string;
  children: JSX.Element;
  isOpen?: boolean;
  icon?: JSX.Element;
  onToggle?: () => void;
};

const CollapseItem: React.FC<CollapseItemProps> = ({ title, children, isOpen, onToggle, icon }) => {
  return (
    <div className={styles.collapseItem}>
      <div className={styles.collapseHeader} onClick={onToggle}>
        {icon && <span className={styles.icon}>{icon}</span>}
        <h3 className={styles.collapseTitle}>{title}</h3>
        <span className={`${styles.collapseIcon} ${isOpen ? styles.collapseIconOpen : ''}`}>+</span>
      </div>
      <div className={`${styles.collapseContent} ${isOpen ? styles.collapseContentOpen : ''}`}>
        <div className={styles.collapseBody}>{children}</div>
      </div>
    </div>
  );
};

const Collapse: React.FC<CollapseProps> = ({ items, allowMultiple = false, defaultOpen = [] }) => {
  const [openItems, setOpenItems] = useState<Set<number>>(() => {
    if (Array.isArray(defaultOpen)) {
      return new Set(defaultOpen.filter((index) => index >= 0 && index < items.length));
    }
    if (typeof defaultOpen === 'number' && defaultOpen >= 0 && defaultOpen < items.length) {
      return new Set([defaultOpen]);
    }
    return new Set<number>();
  });

  const toggleItem = (index: number): void => {
    if (allowMultiple) {
      const newOpenItems = new Set(openItems);
      if (newOpenItems.has(index)) {
        newOpenItems.delete(index);
      } else {
        newOpenItems.add(index);
      }
      setOpenItems(newOpenItems);
    } else {
      setOpenItems(openItems.has(index) ? new Set<number>() : new Set([index]));
    }
  };

  return (
    <div className={styles.collapseContainer}>
      {items.map((item, index) => (
        <CollapseItem
          key={index}
          title={item.title}
          icon={item.icon}
          isOpen={openItems.has(index)}
          onToggle={() => toggleItem(index)}
        >
          {item.children}
        </CollapseItem>
      ))}
    </div>
  );
};

export const BuilderIoCollapseInputs = [
  {
    name: 'accordionItems',
    type: 'list',
    subFields: [
      {
        type: 'string',
        name: 'title',
        required: true,
      },
      {
        type: 'richText',
        name: 'content',
        required: true,
      },
      {
        type: 'boolean',
        name: 'isDefaultOpen',
      },
    ],
  },
];

export const BuilderIoCollapse: FC<{
  accordionItems: { title: string; isDefaultOpen: boolean; content: string }[];
}> = ({ accordionItems }) => {
  const items = useMemo(
    () =>
      accordionItems?.map((accordionItem, index) => ({
        key: index,
        title: accordionItem.title,
        children: <div dangerouslySetInnerHTML={{ __html: accordionItem.content }} />,
        defaultOpen: accordionItem.isDefaultOpen,
      })) || [],
    [accordionItems]
  );

  const defaultOpen = useMemo(() => {
    return items.map((item, index) => (item.defaultOpen ? index : null)).filter((item) => item !== null);
  }, [items]);

  return (
    <div>
      <Collapse defaultOpen={defaultOpen as number[]} items={items} allowMultiple={true} />
    </div>
  );
};
